/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { PureComponent } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import { Query } from '@apollo/client/react/components';
import IconNavBar, { IIconNavBarItem } from '@opendoor/bricks/deprecated/UNSAFE_layout/IconNavBar';

import BuyerAppointmentsSection from '../../components/BuyerAppointmentsSection';
import ContactsPanel from '../../components/ContactsPanel';
import CustomersSection from '../../components/CustomersSection';
import DocumentsSection from '../../components/DocumentsSection';
import FaqSection from '../../components/FaqSection';
import FullPageHomeLoader from '../../components/FullPageHomeLoader';
import {
  CogInHomeIcon,
  ContractIcon,
  DisclosuresIcon,
  LifeSaverIcon,
  LinkIcon,
  PeopleIcon,
  PersonInHomeIcon,
  SummaryIcon,
} from '../../components/icons';
import Layout, { ReferralDashboardOverrideStyles } from '../../components/Layout';
import LearningCenterSection from '../../components/LearningCenterSection';
import NotificationPreferencesSection from '../../components/NotificationPreferencesSection';
import ProductEligibilitySection from '../../components/ProductEligibilitySection';
import ReferCustomersSection from '../../components/ReferCustomersSection';
import { Tab } from '../../components/Tab';
import {
  GET_REFERRALS_DATA,
  IGetReferralDashboardData,
} from '../../graphql/queries/GetReferrralDashboardData';
import { RouterProps } from '../../types';
import { PrivateRouteProps } from '../PrivateRoute';
import { ERROR_PAGE_ROUTE } from '../routes';
import { FAQS } from './faqs';

const BASE_PATH = '/referral-dashboard';
export const REFERRAL_DASHBOARD_NAME = 'referral dashboard';

interface ITab extends IIconNavBarItem {
  component: React.ComponentType;
  path: string;
}

class ReferralDashboard extends PureComponent<RouterProps> {
  public static isAuthorized: PrivateRouteProps['isAuthorized'] = (human) =>
    human.has_partnership_referral_dashboard;

  public static dashboardName: string = REFERRAL_DASHBOARD_NAME;

  public render() {
    return (
      <Query<IGetReferralDashboardData> query={GET_REFERRALS_DATA}>
        {({ loading, error, data }) => {
          let renderSide;

          // eslint-disable-next-line react/display-name
          let renderContent = (tabs: Array<ITab>) => () => {
            return (
              <Switch>
                <Redirect exact from={BASE_PATH} to={BASE_PATH + tabs[0].path} />
                {tabs.map((tab) => (
                  <Route key={tab.link} exact path={tab.link} component={tab.component} />
                ))}
              </Switch>
            );
          };

          // eslint-disable-next-line react/display-name
          const renderNav = (tabs: Array<ITab>) => () => {
            return <IconNavBar navBarItems={tabs} />;
          };

          const handleClose = () => {
            this.props.history.push('/referral-dashboard');
          };

          if (loading) {
            renderContent = () => () => {
              return <FullPageHomeLoader />;
            };
          }

          if (error) {
            return <Redirect to={ERROR_PAGE_ROUTE} />;
          }

          if (data && data.organization!.partnershipType === 'HOME_BUILDER') {
            renderSide = this.renderSide;
          }

          let links: Array<ITab> = [
            {
              name: 'Referral Link',
              icon: <LinkIcon />,
              link: BASE_PATH + '/referral-link',
              path: '/referral-link',
              component: () => <ReferCustomersSection organization={data!.organization!} />,
              render: this.renderTab,
            },
            {
              name: 'Home Eligibility',
              icon: <CogInHomeIcon />,
              link: BASE_PATH + '/home-eligibility',
              path: '/home-eligibility',
              component: () => <ProductEligibilitySection organization={data!.organization!} />,
              render: this.renderTab,
            },
            {
              name: 'Opendoor Customers',
              icon: <SummaryIcon />,
              link: BASE_PATH + '/customers',
              path: '/customers',
              component: () => <CustomersSection />,
              render: this.renderTab,
            },
          ];

          // Only a subset of homebuilder partners are part of the pilot.
          if (data?.organization?.inPartnerBuyerLeadPilot) {
            links = links.concat([
              {
                name: 'Buyer Appointments',
                icon: <PersonInHomeIcon />,
                link: BASE_PATH + '/buyer-appointments',
                path: '/buyer-appointments',
                component: () => <BuyerAppointmentsSection />,
                render: this.renderTab,
              },
            ]);
          }

          links = links.concat({
            name: 'Email Preferences',
            icon: <DisclosuresIcon />,
            link: BASE_PATH + '/subscription-preferences',
            path: '/subscription-preferences',
            component: () => <NotificationPreferencesSection handleClose={handleClose} />,
            render: this.renderTab,
          });

          links = links.concat([
            {
              name: 'FAQs',
              icon: <LifeSaverIcon />,
              link: BASE_PATH + '/faqs',
              path: '/faqs',
              component: () => <FaqSection faqs={FAQS} />,
              render: this.renderTab,
            },
          ]);

          // These sections are only displayed to homebuilder users, so tabs need to hide as well
          if (data?.organization?.partnershipType === 'HOME_BUILDER') {
            links = links.concat(
              {
                name: 'Documents',
                icon: <ContractIcon />,
                link: BASE_PATH + '/documents',
                path: '/documents',
                component: DocumentsSection,
                render: this.renderTab,
              },
              {
                name: 'Learning Center',
                icon: <PeopleIcon />,
                link: BASE_PATH + '/learning-center',
                path: '/learning-center',
                component: LearningCenterSection,
                render: this.renderTab,
              },
            );
          }

          return (
            <>
              <ReferralDashboardOverrideStyles />
              <Layout
                renderContent={renderContent(links)}
                renderNav={renderNav(links)}
                renderSide={renderSide}
              />
            </>
          );
        }}
      </Query>
    );
  }

  private renderSide = () => {
    return <ContactsPanel />;
  };

  private renderTab = (item: IIconNavBarItem, content: JSX.Element, key: string) => (
    <Tab item={item} content={content} key={key} />
  );
}

export default ReferralDashboard;
